import React, { useMemo } from 'react';
import { Box, Button, FormControl, FormControlLabel, FormLabel, Paper, Radio, RadioGroup, Stack } from '@mui/material';

import { RoofPlane } from '@paragon/contract-library';
import { PlaneWithCalculatedGeometry } from '@paragon/layout';
import { PropertiesPanel } from '@paragon/layout-models';
import { InlineNotification } from '@paragon/notification-tools';

import { matchFasciaHeights } from '../../../library/layout-geometry/fascia-heights';
import { getSelectedItemIdentifiers } from '../../../library/selection/selectors';
import { editRoofPlanes } from '../../../store/actions/layout/roofPlaneActions';
import { getCalculatedRoofAndCeilingPlanes } from '../../../store/selectors/roofAndCeilingPlaneSelectors';
import { setFasciaHeightAdjustmentMode, setMatchFasciaHeightsPanelClosed } from '../../../store/slices/panelDataSlice';
import { PlanePickerField } from '../../controls/PlanePickerField';
import { useAppDispatch, useAppSelector } from '../../hooks';

export function MatchFasciaHeightsPanel() {
  const dispatch = useAppDispatch();

  const selectedItems = useAppSelector(getSelectedItemIdentifiers);
  const calculatedPlanes = useAppSelector(getCalculatedRoofAndCeilingPlanes);
  const { mode, referencePlaneGuid } = useAppSelector((state) => state.panelData.matchFasciaHeightsPanelState!);

  const changesIfApplied = useMemo(() => {
    const selectedRoofPlanes = selectedItems.filter((item) => item.type === 'roofPlane');

    if (referencePlaneGuid == null) return null;
    if (selectedRoofPlanes.length === 0) return null;

    const referencePlane = calculatedPlanes[referencePlaneGuid] as PlaneWithCalculatedGeometry<RoofPlane>;
    const planesToAdjust = selectedRoofPlanes
      .map((item) => item.guid)
      .map((guid) => calculatedPlanes[guid] as PlaneWithCalculatedGeometry<RoofPlane>);

    return matchFasciaHeights(referencePlane, planesToAdjust, { mode });
  }, [calculatedPlanes, mode, referencePlaneGuid, selectedItems]);

  const canApplyAdjustment = changesIfApplied && !('error' in changesIfApplied);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PropertiesPanel title="Match Fascia Heights">
        <Stack direction="column" spacing={2} sx={{ margin: 2 }} className="clickable-during-picking">
          <FormControl>
            <FormLabel>Adjustment Method</FormLabel>
            <RadioGroup
              row
              value={mode}
              onChange={(_, mode) => {
                dispatch(setFasciaHeightAdjustmentMode(mode as 'overhang' | 'heel-height'));
              }}
            >
              <FormControlLabel value="heel-height" control={<Radio />} label="Heel Height / Butt Cut" />
              <FormControlLabel value="overhang" control={<Radio />} label="Overhang" />
            </RadioGroup>
          </FormControl>

          <PlanePickerField
            label="Reference Plane"
            value={referencePlaneGuid}
            target={{ type: 'fascia-height-reference' }}
          />

          {/* uncomment this to see a preview of changes for reference during development */}
          {/*<pre style={{ overflow: 'auto' }}>{JSON.stringify(changesIfApplied, null, 2)}</pre>*/}

          {changesIfApplied && 'error' in changesIfApplied && (
            <InlineNotification type="error">{changesIfApplied.error}</InlineNotification>
          )}
        </Stack>
      </PropertiesPanel>

      <Paper elevation={1} sx={{ display: 'flex', justifyContent: 'flex-end', padding: 1 }}>
        <Button sx={{ m: 1 }} variant="outlined" onClick={() => void dispatch(setMatchFasciaHeightsPanelClosed())}>
          Cancel
        </Button>

        <Button
          sx={{ m: 1 }}
          variant="contained"
          disabled={referencePlaneGuid == null || !canApplyAdjustment}
          onClick={async () => {
            if (Array.isArray(changesIfApplied)) {
              await dispatch(editRoofPlanes(changesIfApplied));
              dispatch(setMatchFasciaHeightsPanelClosed());
            }
          }}
        >
          Adjust
        </Button>
      </Paper>
    </Box>
  );
}
